// 분배금 현황 화면 (전년도 비교)
// 조회조건: 계좌 | 연도 | 조회 | 금액/비율 | 이자포함 | 전년합계 | 당해합계
// 그리드: 당해연도 월별 분배금 + 투자잔액
// 차트: 당해연도 vs 전년도 묶음 막대그래프
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { fetchDividendStatus } from '../../api/dividendStatus';
import { fetchAccounts }       from '../../api/accounts';
import PageLayout              from '../../components/PageLayout';
import './DividendSummaryPage.css';

const MONTHS      = [1,2,3,4,5,6,7,8,9,10,11,12];
const CURR_YEAR   = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURR_YEAR - i);

// ── 포맷 헬퍼 ────────────────────────────────────────────────
const fNum = (v) =>
  v != null && Number(v) !== 0
    ? Number(v).toLocaleString('ko-KR')
    : '';

const fRate = (v) =>
  v != null && Number(v) !== 0
    ? `${Number(v).toFixed(2)}%`
    : '';

// 그리드 셀 값: amount → curr_months, rate → curr_rates (백엔드 계산값)
const gridVal = (row, m, mode) => {
  if (mode === 'amount') return row.curr_months?.[String(m)] ?? null;
  return row.curr_rates?.[String(m)] ?? null;
};


const DividendSummaryPage = ({ isActive }) => {
  const [year,            setYear]            = useState(CURR_YEAR);
  const [filterAccount,   setFilterAccount]   = useState('');
  const [accounts,        setAccounts]        = useState([]);
  const [viewMode,        setViewMode]        = useState('amount');
  const [includeInterest, setIncludeInterest] = useState(true);
  const [rows,            setRows]            = useState([]);
  const [monthlySummary,  setMonthlySummary]  = useState({ curr: {}, prev: {} });
  const [currYearState,   setCurrYearState]   = useState(CURR_YEAR);
  const [prevYearState,   setPrevYearState]   = useState(CURR_YEAR - 1);
  const [currYearTotal,   setCurrYearTotal]   = useState(0);
  const [prevYearTotal,   setPrevYearTotal]   = useState(0);
  const [loading,         setLoading]         = useState(false);
  const [message,         setMessage]         = useState({ text: '', type: '' });
  const [acctWidth,       setAcctWidth]       = useState(90);
  const [nameWidth,       setNameWidth]       = useState(200);
  const resizingRef = useRef(null);

  // 계좌 목록 로드 (마운트 + 탭 활성화 시)
  useEffect(() => {
    fetchAccounts().then(setAccounts).catch(() => {});
  }, []);
  useEffect(() => {
    if (isActive) fetchAccounts().then(setAccounts).catch(() => {});
  }, [isActive]);

  const showMessage = (text, type = 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // ── 데이터 조회 ────────────────────────────────────────────
  // year / filterAccount / includeInterest 변경 시 자동 재생성 → 자동 재조회
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year, include_interest: includeInterest };
      if (filterAccount && filterAccount !== '전체') params.account_name = filterAccount;
      const res = await fetchDividendStatus(params);
      setRows(res.data || []);
      setMonthlySummary(res.monthly_summary || { curr: {}, prev: {} });
      setCurrYearState(res.curr_year  ?? year);
      setPrevYearState(res.prev_year  ?? year - 1);
      setCurrYearTotal(res.curr_year_total ?? 0);
      setPrevYearTotal(res.prev_year_total ?? 0);
    } catch {
      showMessage('조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year, filterAccount, includeInterest]);

  // 자동 재조회 (year / account / includeInterest 변경 시)
  useEffect(() => { loadData(); }, [loadData]);

  // ── 합계 행 (useMemo) ─────────────────────────────────────
  const totals = useMemo(() => {
    const t = {};
    for (const m of MONTHS) {
      t[`m${m}`] = rows.reduce((sum, row) => sum + (gridVal(row, m, viewMode) ?? 0), 0);
    }
    t.annual = MONTHS.reduce((sum, m) => sum + (t[`m${m}`] ?? 0), 0);
    return t;
  }, [rows, viewMode]);

  // ── 차트 데이터 (useMemo) ──────────────────────────────────
  const chartData = useMemo(() =>
    MONTHS.map(m => {
      let currVal, prevVal;
      if (viewMode === 'amount') {
        currVal = monthlySummary.curr?.[String(m)] ?? 0;
        prevVal = monthlySummary.prev?.[String(m)] ?? 0;
      } else {
        currVal = rows.reduce((sum, row) =>
          sum + (row.curr_rates?.[String(m)] ?? 0), 0);
        prevVal = rows.reduce((sum, row) =>
          sum + (row.prev_rates?.[String(m)] ?? 0), 0);
      }
      return { month: `${m}월`, curr: currVal, prev: prevVal };
    }),
    [rows, monthlySummary, viewMode]
  );

  // ── 컬럼 리사이즈 ───────────────────────────────────────────
  const startResize = (e, field, currentW) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    resizingRef.current = { field, startX, startW: currentW };
    const onMove = (ev) => {
      if (!resizingRef.current) return;
      const { field: f, startX: sx, startW: sw } = resizingRef.current;
      const newW = Math.max(60, sw + ev.clientX - sx);
      if (f === 'acct') setAcctWidth(newW);
      if (f === 'name') setNameWidth(newW);
    };
    const onUp = () => {
      resizingRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // freeze 컬럼 left 오프셋
  const c2Left = acctWidth;
  const c3Left = acctWidth + nameWidth;

  // ── Y축 포맷 ──────────────────────────────────────────────
  const yAxisFmt = (v) =>
    viewMode === 'amount'
      ? (v >= 10000 ? `${(v / 10000).toFixed(0)}만` : v.toLocaleString('ko-KR'))
      : `${v.toFixed(1)}%`;

  // ── 커스텀 툴팁 ───────────────────────────────────────────
  const renderTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const cv = payload.find(p => p.dataKey === 'curr')?.value ?? 0;
    const pv = payload.find(p => p.dataKey === 'prev')?.value ?? 0;
    const diff = cv - pv;
    const fmt = (n) => viewMode === 'amount'
      ? fNum(Math.abs(n)) + '원'
      : Math.abs(n).toFixed(2) + '%';
    return (
      <div className="ds-chart-tooltip">
        <p className="ds-tt-label">{label}</p>
        <p className="ds-tt-prev">{prevYearState}년: {fmt(pv)}</p>
        <p className="ds-tt-curr">{currYearState}년: {fmt(cv)}</p>
        <p className={diff >= 0 ? 'ds-tt-diff-pos' : 'ds-tt-diff-neg'}>
          전년대비: {diff >= 0 ? '+' : '-'}{fmt(Math.abs(diff))}
        </p>
      </div>
    );
  };

  // ── 툴바 ────────────────────────────────────────────────────
  const toolbar = (
    <div className="ds-toolbar">
      {/* 계좌 */}
      <select
        className="ds-select"
        value={filterAccount}
        onChange={(e) => setFilterAccount(e.target.value)}
      >
        <option value="">전체</option>
        {accounts.map(a => (
          <option key={a.account_name} value={a.account_name}>{a.account_name}</option>
        ))}
      </select>

      {/* 연도 */}
      <select
        className="ds-select ds-year-select"
        value={year}
        onChange={(e) => setYear(Number(e.target.value))}
      >
        {YEAR_OPTIONS.map(y => (
          <option key={y} value={y}>{y}년</option>
        ))}
      </select>

      {/* 조회 버튼 */}
      <button className="btn btn-primary" onClick={loadData} disabled={loading}>
        {loading ? '조회 중...' : '조회'}
      </button>

      {/* 금액/비율 라디오 */}
      <div className="ds-radio-group">
        <label className="ds-radio-label">
          <input type="radio" name="divViewMode" value="amount"
            checked={viewMode === 'amount'} onChange={() => setViewMode('amount')} />
          금액
        </label>
        <label className="ds-radio-label">
          <input type="radio" name="divViewMode" value="rate"
            checked={viewMode === 'rate'} onChange={() => setViewMode('rate')} />
          비율
        </label>
      </div>

      {/* 이자포함 체크박스 */}
      <label className="ds-check-label">
        <input type="checkbox" checked={includeInterest}
          onChange={(e) => setIncludeInterest(e.target.checked)} />
        이자포함
      </label>

      {/* 연도 합계 */}
      {rows.length > 0 && (
        <>
          <span className="ds-year-summary ds-prev-summary">
            {prevYearState}년: {prevYearTotal > 0 ? fNum(prevYearTotal) + ' 원' : '-'}
          </span>
          <span className="ds-year-summary ds-curr-summary">
            {currYearState}년: {currYearTotal > 0 ? fNum(currYearTotal) + ' 원' : '-'}
          </span>
        </>
      )}
    </div>
  );

  // ── 렌더 ────────────────────────────────────────────────────
  return (
    <PageLayout title="분배금현황" toolbar={toolbar} message={message}>

      {/* 그리드 */}
      <div className="ds-grid-wrapper">
        <table className="grid-table ds-grid">
          <thead>
            <tr>
              <th className="col-freeze ds-freeze-c1"
                style={{ width: acctWidth, minWidth: acctWidth, left: 0 }}>
                계좌명
                <span className="col-resize-handle"
                  onMouseDown={(e) => startResize(e, 'acct', acctWidth)} />
              </th>
              <th className="col-freeze ds-freeze-c2"
                style={{ width: nameWidth, minWidth: nameWidth, left: c2Left }}>
                종목명
                <span className="col-resize-handle"
                  onMouseDown={(e) => startResize(e, 'name', nameWidth)} />
              </th>
              <th className="col-freeze ds-freeze-c3 col-center"
                style={{ width: 65, left: c3Left }}>티커</th>
              {viewMode === 'amount' && (
                <th className="col-right" style={{ width: 100 }}>투자잔액</th>
              )}
              <th className="col-right" style={{ width: 90 }}>연합계</th>
              {MONTHS.map(m => (
                <th key={m} className="col-right" style={{ width: 72 }}>{m}월</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* 합계 행 (sticky, 맨 위) */}
            {rows.length > 0 && (
              <tr className="ds-total-row">
                <td className="col-freeze ds-freeze-c1 ds-total-label"
                  style={{ left: 0 }}>합계</td>
                <td className="col-freeze ds-freeze-c2"
                  style={{ left: c2Left }} />
                <td className="col-freeze ds-freeze-c3"
                  style={{ left: c3Left }} />
                {viewMode === 'amount' && <td className="col-right" />}
                <td className="col-right ds-total-cell">
                  {viewMode === 'amount' ? fNum(totals.annual) : fRate(totals.annual)}
                </td>
                {MONTHS.map(m => (
                  <td key={m} className="col-right ds-total-cell">
                    {viewMode === 'amount' ? fNum(totals[`m${m}`]) : fRate(totals[`m${m}`])}
                  </td>
                ))}
              </tr>
            )}

            {/* 빈 메시지 */}
            {rows.length === 0 && (
              <tr>
                <td colSpan={viewMode === 'amount' ? 17 : 16} className="grid-empty">
                  {loading ? '조회 중...' : '계좌와 연도를 선택하고 조회하세요.'}
                </td>
              </tr>
            )}

            {/* 데이터 행 */}
            {rows.map((row, idx) => {
              const isDeposit = row.stock_type === 'DEPOSIT' || row.stock_type === 'CMA';
              const annual = viewMode === 'amount'
                ? (row.curr_total || 0)
                : MONTHS.reduce((sum, m) => sum + (gridVal(row, m, viewMode) ?? 0), 0);

              return (
                <tr key={`${row.account_name}__${row.ticker}__${idx}`}
                  className={isDeposit ? 'ds-row-deposit' : ''}>
                  <td className="col-freeze ds-freeze-c1"
                    style={{ left: 0 }}>{row.account_name}</td>
                  <td className="col-freeze ds-freeze-c2 col-ellipsis"
                    style={{ left: c2Left }}
                    title={row.stock_name || row.ticker}>
                    {row.stock_name || row.ticker}
                  </td>
                  <td className="col-freeze ds-freeze-c3 col-center"
                    style={{ left: c3Left }}>{row.ticker}</td>
                  {viewMode === 'amount' && (
                    <td className="col-right ds-readonly">
                      {row.invest_balance > 0 ? fNum(row.invest_balance) : ''}
                    </td>
                  )}
                  <td className="col-right ds-annual">
                    {annual > 0
                      ? (viewMode === 'amount' ? fNum(annual) : fRate(annual))
                      : ''}
                  </td>
                  {MONTHS.map(m => {
                    const val = gridVal(row, m, viewMode);
                    return (
                      <td key={m} className="col-right">
                        {val != null && val > 0
                          ? (viewMode === 'amount' ? fNum(val) : fRate(val))
                          : ''}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 하단 막대그래프 (전년 vs 당해년도) */}
      {rows.length > 0 && (
        <div className="ds-chart-wrapper">
          <div className="ds-chart-title">
            월별 합계 — {prevYearState}년 vs {currYearState}년
            &nbsp;({viewMode === 'amount' ? '금액' : '비율'})
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={yAxisFmt} />
              <Tooltip content={renderTooltip} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="prev" name={`${prevYearState}년`}
                fill="#b0bec5" barSize={12} radius={[2, 2, 0, 0]} />
              <Bar dataKey="curr" name={`${currYearState}년`}
                fill="#4a90e2" barSize={12} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PageLayout>
  );
};

export default DividendSummaryPage;
